// Color utilities for dynamic theme colors

// Convert hex color to RGB values
export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

// Get CSS custom properties for dynamic colors
export const getCustomColorStyles = (primaryColor) => {
  if (!primaryColor) return {};
  
  const rgb = hexToRgb(primaryColor);
  if (!rgb) return {};
  
  return {
    '--primary-color': primaryColor,
    '--primary-color-rgb': `${rgb.r}, ${rgb.g}, ${rgb.b}`,
    '--primary-color-hover': adjustBrightness(primaryColor, -0.1),
    '--primary-color-light': adjustBrightness(primaryColor, 0.2)
  };
};

// Adjust color brightness (amount between -1 and 1)
export const adjustBrightness = (hex, amount) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  
  const adjust = (val) => Math.max(0, Math.min(255, val + (amount * 255)));
  
  const newR = Math.round(adjust(rgb.r));
  const newG = Math.round(adjust(rgb.g));
  const newB = Math.round(adjust(rgb.b));
  
  return `#${((1 << 24) + (newR << 16) + (newG << 8) + newB).toString(16).slice(1)}`;
};

// Dynamic button classes based on primary color
export const getDynamicButtonClasses = (primaryColor, isHover = false) => {
  if (!primaryColor) {
    return isHover ? 'bg-yellow-400' : 'bg-yellow-500 hover:bg-yellow-400';
  }
  
  const hoverColor = adjustBrightness(primaryColor, -0.1);
  
  return {
    backgroundColor: isHover ? hoverColor : primaryColor,
    transition: 'background-color 0.2s ease-in-out'
  };
};

// Dynamic text color classes
export const getDynamicTextClasses = (primaryColor) => {
  if (!primaryColor) return 'text-yellow-500';
  
  return {
    color: primaryColor
  };
};

// Check if color is light or dark (for text contrast)
export const isLightColor = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  
  // Calculate relative luminance
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.5;
};

// Get contrast text color (black or white)
export const getContrastTextColor = (hex) => {
  return isLightColor(hex) ? '#000000' : '#FFFFFF';
};
// Black text on light tag backgrounds, white on dark
export const tagTextColor = (hex) => {
  const rgb = hexToRgb(hex || '#888888');
  if (!rgb) return '#FFFFFF';
  return rgb.r * 0.299 + rgb.g * 0.587 + rgb.b * 0.114 > 160 ? '#000000' : '#FFFFFF';
};
